import React, {ComponentType} from 'react';
import {type AtomRenderProps} from '@packages/systems/wfdl';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {useRuntimeEnvironment} from '@packages/systems/render';

/**
 * An atom compatible component that only renders a div
 * with the provided attributes. This is desirable for certain atoms
 * when in an LLM environment where their children should not be sent
 * to the LLM.
 */
export const WrapperOnlyRender = ({
  attributes,
  classes,
  generateAttributes,
  tag: Tag = 'div',
}: AtomRenderProps & {tag?: string}) => {
  const attributesToApply = generateAttributes(attributes, classes);
  // @ts-expect-error -- 2559 -- Type '{ style?: StyleAttributeObject | undefined; }' has no properties in common with type 'IntrinsicAttributes'.ts
  return <Tag {...attributesToApply} />;
};

/**
 * Given an Atom render component, returns a new component that will render the
 * original component when the runtime environment is not LLM. If the runtime
 * environment is LLM, it will render the provided LlmComponent if it exists,
 * otherwise it will render a wrapper div and no children.
 *
 * @param WrappedComponent The component you would normally pass to `Atom.createRender`
 * @param LlmComponent An optional component or tag type to render when the runtime environment is LLM
 * @returns A new component that will render the original component when the runtime environment is not LLM
 */
export function withLlmRender<
  PropType extends AtomRenderProps = AtomRenderProps,
>(
  WrappedComponent: ComponentType<PropType>,
  LlmComponent?: string | ComponentType<PropType>
) {
  return (props: PropType) => {
    const runtimeEnvironment = useRuntimeEnvironment();

    if (runtimeEnvironment === WebflowEnvironments.LLM) {
      if (LlmComponent) {
        if (typeof LlmComponent === 'string') {
          return <WrapperOnlyRender tag={LlmComponent} {...props} />;
        }
        return <LlmComponent {...props} />;
      }
      return <WrapperOnlyRender {...props} />;
    }
    return <WrappedComponent {...props} />;
  };
}
